import type { OrderEvent } from '@/api/orders';
import { fmtDate, money } from './lib';

/** The run's journal entries — hidden from the Activity table until asked for. */
export const ROUTINE_EVENT_TYPES = new Set(['note', 'correction']);

const EVENT_LABELS: Record<string, string> = {
  announcement: 'Announced',
  placed: 'Placed',
  confirmation: 'Confirmed',
  invoice: 'Invoice',
  shipment: 'Shipped',
  refund: 'Refund',
  backorder_notice: 'Backorder notice',
  receiving_matched: 'Received',
  reconciled: 'Balanced',
  note: 'Note',
  correction: 'Correction',
  wont_order: "Won't order",
  lapsed: 'Lapsed',
  document_fetched: 'Document fetched',
  document_uploaded: 'Document uploaded',
  carrier_charge: 'Carrier charge',
  manual_link: 'Email linked',
};

export function eventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type.replace(/_/g, ' ');
}

/**
 * The fact line for a milestone, from `event.data` — never from the summary.
 * Missing keys are simply left out, so a pre-contract event renders as its
 * label alone and its prose stays behind the Note toggle.
 */
export function eventDetail(event: OrderEvent, currency: string): string | null {
  const d: Record<string, unknown> = event.data ?? {};
  const str = (k: string): string | null =>
    typeof d[k] === 'string' || typeof d[k] === 'number' ? String(d[k]) : null;
  const num = (k: string): number | null => {
    if (d[k] == null || d[k] === '') return null;
    const v = Number(d[k]);
    return Number.isFinite(v) ? v : null;
  };
  const count = (k: string, noun: string): string | null => {
    const n = num(k);
    return n == null ? null : `${n} ${noun}${n === 1 ? '' : 's'}`;
  };
  const amount = (k: string): string | null => {
    const n = num(k);
    return n == null ? null : money(n, currency);
  };

  const parts: Array<string | null> = [];
  switch (event.eventType) {
    case 'announcement':
      parts.push(count('lines', 'product'), count('releases', 'release'));
      break;
    case 'placed':
    case 'confirmation':
      parts.push(str('order_no') && `order ${str('order_no')}`, count('lines', 'product'), count('units', 'unit'), amount('total'));
      break;
    case 'invoice':
      parts.push(str('invoice_no'), count('lines', 'product'), amount('total'));
      break;
    case 'shipment':
      parts.push(
        [str('carrier'), str('tracking_code')].filter(Boolean).join(' ') || null,
        count('parcels', 'parcel'),
        str('eta') && `ETA ${fmtDate(str('eta'))}`
      );
      break;
    case 'refund':
      parts.push(amount('amount'), str('invoice_no'));
      break;
    case 'receiving_matched':
      parts.push(str('receiving_id') && `#${str('receiving_id')}`, count('lines', 'product'), amount('total'));
      break;
    case 'reconciled':
      parts.push(str('receiving_id') && `#${str('receiving_id')}`, count('edits', 'edit'), amount('delta'));
      break;
    case 'document_fetched':
    case 'document_uploaded':
      parts.push(str('filename'), str('doc_hint')?.replace(/_/g, ' ') ?? null);
      break;
    case 'carrier_charge':
      parts.push(str('carrier')?.toUpperCase() ?? null, str('invoice_no'), amount('amount'));
      break;
    default:
      break;
  }
  const line = parts.filter(Boolean).join(' · ');
  return line || null;
}
